import { useEffect, useRef } from "react";

/**
 * Horizontal multi-step indicator for Plan Manager setup wizard.
 * Presentational — emits step clicks via `onClickHandler`.
 *
 * Validation states (via `step.isComplete`):
 * - `complete` — passes validation or initial un-evaluated state (green)
 * - `incomplete` — evaluated but missing required fields / invalid (red)
 *
 * @param {object} props
 * @param {Array<{ label: string, isComplete?: boolean }>} props.steps
 * @param {number} props.activeStep - 1-based active step index.
 * @param {(stepIndex: number) => void} props.onClickHandler - Receives 0-based index.
 * @returns {import('react').JSX.Element}
 */
const SetupStepper = ({ steps, activeStep, onClickHandler }) => {
    const activeElementRef = useRef(null);
    useEffect(() => {
        if (typeof activeElementRef.current?.scrollIntoView === "function") {
            activeElementRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [activeStep]);

    return (
        <div className="steps">
            {steps?.map((step, i) => {
                const isActive = activeStep === i + 1;
                const isReached = activeStep > i;
                const isComplete = step?.isComplete !== false;

                const stepClassName = [
                    "step",
                    isReached ? "prev" : null,
                    isActive ? "active" : null,
                    isComplete ? "complete" : "incomplete",
                ]
                    .filter(Boolean)
                    .join(" ");

                return (
                    <div
                        ref={isActive ? activeElementRef : null}
                        key={step?.label}
                        onClick={() => onClickHandler(i)}
                        className={stepClassName}
                        aria-current={isActive ? "step" : undefined}
                        data-complete={isComplete ? "true" : "false"}
                        data-status={isComplete ? "complete" : "incomplete"}
                    >
                        <p className="indicator">{i + 1}</p>
                        <label>{step?.label}</label>
                    </div>
                );
            })}
        </div>
    );
};

export default SetupStepper;
